<template>
  <!-- Phone hand-off: hero card at the top of the page, plus a bottom bar
  that repeats the Copy Link CTA once the card has scrolled out of view. One
  component so both share the copied state. -->
  <div>
    <section
      ref="hero"
      class="p-4 rounded-2xl border border-border-normal bg-white shadow-sm"
    >
      <!-- Phone → computer illustration -->
      <div
        class="py-4 rounded-xl bg-gradient-to-br from-primary-blue-10 to-white flex items-center justify-center gap-3 text-primary-blue-100"
        aria-hidden="true"
      >
        <MobileDeviceIcon
          :width="28"
          :height="28"
        />
        <div class="flex items-center gap-1.5">
          <span
            v-for="n in 5"
            :key="n"
            class="handoff-dot w-1 h-1 rounded-full bg-primary-blue-100"
            :style="{ animationDelay: `${(n - 1) * 150}ms` }"
          />
        </div>
        <DesktopDeviceIcon
          :width="32"
          :height="32"
        />
      </div>

      <BaseText
        type="label"
        size="md"
        class="text-text-loud mt-3"
      >
        {{ copy.title }}
      </BaseText>
      <BaseText
        type="body"
        size="sm"
        class="text-text-normal mt-1"
      >
        {{ copy.body }}
      </BaseText>
      <BaseText
        type="body"
        size="sm"
        weight="semibold"
        class="text-text-loud mt-1"
      >
        {{ copy.bodyEmphasis }}
      </BaseText>

      <BaseButton
        primary
        class="w-full mt-3"
        @click="copyLink('hero')"
      >
        {{ copiedSource === 'hero' ? copy.copiedLabel : copy.copyLabel }}
      </BaseButton>
    </section>

    <transition name="fade">
      <div
        v-if="heroScrolledPast"
        class="phone-handoff-bar fixed right-0 bottom-0 z-40 px-4 pt-3 border-t border-border-normal bg-white"
        :style="barStyle"
      >
        <BaseText
          type="body"
          size="xs"
          class="text-text-subdued text-center"
        >
          {{ copy.stickyLabel }}
        </BaseText>
        <BaseButton
          primary
          class="w-full mt-2"
          @click="copyLink('sticky')"
        >
          {{ copiedSource === 'sticky' ? copy.copiedLabel : copy.copyLabel }}
        </BaseButton>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { copyTextAsync } from '@/utils/clipboard'
import posthog from 'posthog-js'
import { SETUP_PHONE } from '@/utils/setup/setupContent'
import MobileDeviceIcon from '@/components/globals/Icons/MobileDeviceIcon'
import DesktopDeviceIcon from '@/components/globals/Icons/DesktopDeviceIcon'

const COPIED_RESET_MS = 2500
// Must match .sidebar-root widths in Sidebar.vue
const SIDEBAR_WIDTH = 288
const SIDEBAR_COLLAPSED_WIDTH = 76

// For browsers without the async Clipboard API (insecure contexts, older
// in-app webviews)
function copyWithSelection (text) {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  textarea.setSelectionRange(0, text.length)
  let copied = false
  try {
    copied = document.execCommand('copy')
  } catch (error) {
    copied = false
  }
  document.body.removeChild(textarea)
  return copied
}

export default {
  name: 'SetupPhoneHandoff',
  components: {
    MobileDeviceIcon,
    DesktopDeviceIcon
  },
  data () {
    return {
      copy: SETUP_PHONE,
      // 'hero' | 'sticky' while that button shows the copied label
      copiedSource: null,
      heroScrolledPast: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['isUserLoggedIn']),
    ...mapGetters('MiscModule', ['getSidebarCollapsed', 'getSidebarCollapseDuration']),
    // Same condition App.vue uses to render the sidebar
    sidebarShown () {
      return this.isUserLoggedIn && !!this.$route.meta.requiresAuth && !this.$route.meta.hideSidebar
    },
    barStyle () {
      const width = this.getSidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH
      return {
        left: this.sidebarShown ? `${width}px` : '0px',
        '--collapse-transition-duration': `${this.getSidebarCollapseDuration}ms`
      }
    }
  },
  mounted () {
    if (typeof IntersectionObserver === 'undefined' || !this.$refs.hero) return
    this.heroObserver = new IntersectionObserver(([entry]) => {
      // Only once the card is above the viewport, not before it's reached
      this.heroScrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0
    })
    this.heroObserver.observe(this.$refs.hero)
  },
  beforeDestroy () {
    if (this.heroObserver) this.heroObserver.disconnect()
    clearTimeout(this.copiedTimer)
  },
  methods: {
    async copyLink (source) {
      const url = `${window.location.origin}/launchpad?utm_source=setup_phone_copy`
      let copied = false
      try {
        await copyTextAsync(url)
        copied = true
      } catch (error) {
        copied = copyWithSelection(url)
      }

      if (!copied) {
        this.$showAlert({ type: 'info', message: this.copy.copyFallbackMessage.replace('{url}', url) })
        return
      }

      this.copiedSource = source
      clearTimeout(this.copiedTimer)
      this.copiedTimer = setTimeout(() => { this.copiedSource = null }, COPIED_RESET_MS)
      posthog.capture('Setup Desktop Link Copied', { source })
    }
  }
}
</script>

<style scoped>
.phone-handoff-bar {
  padding-bottom: calc(0.75rem + env(safe-area-inset-bottom));
  /* Follows the sidebar's collapse; opacity keeps the global fade working */
  transition: left var(--collapse-transition-duration) ease-in-out, opacity .5s;
}
.handoff-dot {
  opacity: 0.25;
  animation: handoff-dot-travel 1.5s ease-in-out infinite;
}
@keyframes handoff-dot-travel {
  0%, 60%, 100% { opacity: 0.25; }
  30% { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .handoff-dot {
    animation: none;
    opacity: 0.6;
  }
}
</style>
